/**
 * Rendert beim Hovern im Linien-Chart pro Datenreihe ein eigenes Label
 * direkt neben dem jeweiligen Datenpunkt – statt aller Werte in einem Block.
 *
 * Wird als <Customized component={ChartPointLabels} /> in einen recharts
 * LineChart gehaengt. recharts klont das Element mit seinen internen
 * Chart-Props/State (offset, activeTooltipIndex, formattedGraphicalItems).
 */

type SeriesLabelMap = Record<string, string>;

type ChartOffset = {
  top: number;
  left: number;
  width: number;
  height: number;
};

type GraphicalPoint = {
  x: number;
  y: number;
  value?: number | number[];
};

/**
 * recharts legt die berechnete Geometrie in `props` ab, die Konfiguration der
 * Linie (dataKey, stroke) bleibt aber am urspruenglichen React-Element `item`.
 */
type FormattedGraphicalItem = {
  props?: {
    points?: GraphicalPoint[];
  };
  item?: {
    props?: {
      dataKey?: string;
      stroke?: string;
    };
  };
};

type ChartPointLabelsProps = {
  /** dataKey -> Anzeigename, z. B. { count: 'Termine' } */
  labels: SeriesLabelMap;
  // Von recharts injiziert:
  offset?: ChartOffset;
  activeTooltipIndex?: number;
  isTooltipActive?: boolean;
  formattedGraphicalItems?: FormattedGraphicalItem[];
};

const LABEL_HEIGHT = 22;
const LABEL_GAP = 4;
const POINT_DISTANCE = 12;
const PADDING_X = 9;
const DOT_RADIUS = 3;
const NAME_CHAR_WIDTH = 5.9;
const VALUE_CHAR_WIDTH = 6.9;

type ResolvedLabel = {
  dataKey: string;
  name: string;
  value: number;
  color: string;
  x: number;
  y: number;
  width: number;
};

/** Breite ohne DOM-Messung schaetzen – SVG-Text laesst sich nicht vorab messen. */
function estimateWidth(name: string, value: string): number {
  return (
    PADDING_X * 2 +
    DOT_RADIUS * 2 +
    6 +
    name.length * NAME_CHAR_WIDTH +
    5 +
    value.length * VALUE_CHAR_WIDTH
  );
}

/**
 * Schiebt sich ueberlappende Labels auseinander, ohne die Reihenfolge
 * (und damit die Zuordnung zum Punkt) zu verlieren.
 */
function spreadVertically(labels: ResolvedLabel[], offset: ChartOffset): ResolvedLabel[] {
  const sorted = [...labels].sort((a, b) => a.y - b.y);
  const step = LABEL_HEIGHT + LABEL_GAP;

  let cursor = -Infinity;
  const pushedDown = sorted.map((label) => {
    const y = Math.max(label.y, cursor + step);
    cursor = y;
    return { ...label, y };
  });

  // Unten rausgelaufen? Gesamten Stapel nach oben schieben.
  const bottomLimit = offset.top + offset.height - LABEL_HEIGHT / 2;
  const topLimit = offset.top + LABEL_HEIGHT / 2;
  const overflow = (pushedDown[pushedDown.length - 1]?.y ?? 0) - bottomLimit;
  const shift = overflow > 0 ? overflow : 0;

  return pushedDown.map((label) => ({
    ...label,
    y: Math.max(topLimit, label.y - shift),
  }));
}

export default function ChartPointLabels({
  labels,
  offset,
  activeTooltipIndex,
  isTooltipActive,
  formattedGraphicalItems,
}: ChartPointLabelsProps) {
  if (
    !isTooltipActive ||
    !offset ||
    !formattedGraphicalItems ||
    activeTooltipIndex === undefined ||
    activeTooltipIndex < 0
  ) {
    return null;
  }

  const resolved: ResolvedLabel[] = [];

  formattedGraphicalItems.forEach((item) => {
    const dataKey = item.item?.props?.dataKey;
    const point = item.props?.points?.[activeTooltipIndex];
    if (!dataKey || !point || typeof point.value !== 'number') return;
    if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) return;

    const name = labels[dataKey] ?? dataKey;
    const value = point.value;
    resolved.push({
      dataKey,
      name,
      value,
      color: item.item?.props?.stroke || 'hsl(var(--chart-1))',
      x: point.x,
      y: point.y,
      width: estimateWidth(name, String(value)),
    });
  });

  if (resolved.length === 0) return null;

  const placed = spreadVertically(resolved, offset);
  const rightLimit = offset.left + offset.width;

  return (
    <g pointerEvents="none">
      {placed.map((label) => {
        const fitsRight = label.x + POINT_DISTANCE + label.width <= rightLimit;
        const boxX = fitsRight
          ? label.x + POINT_DISTANCE
          : label.x - POINT_DISTANCE - label.width;
        const boxY = label.y - LABEL_HEIGHT / 2;
        const dotX = boxX + PADDING_X + DOT_RADIUS;
        const textX = dotX + DOT_RADIUS + 6;

        return (
          <g key={label.dataKey} data-series={label.dataKey} data-label-y={label.y}>
            <rect
              x={boxX}
              y={boxY}
              width={label.width}
              height={LABEL_HEIGHT}
              rx={6}
              fill="hsl(var(--popover))"
              stroke="hsl(var(--border))"
              strokeWidth={1}
              opacity={0.97}
            />
            <circle cx={dotX} cy={label.y} r={DOT_RADIUS} fill={label.color} />
            <text
              x={textX}
              y={label.y}
              dominantBaseline="central"
              fontSize={11}
              fill="hsl(var(--muted-foreground))"
            >
              {label.name}
              <tspan
                dx={5}
                fontSize={12}
                fontWeight={600}
                fill="hsl(var(--popover-foreground))"
              >
                {label.value}
              </tspan>
            </text>
          </g>
        );
      })}
    </g>
  );
}
